import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { inject, Injectable, Injector } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, take } from 'rxjs/operators';

import { HTTP_STATUS_CODES } from '../app-constants/http-status-codes';
import { AuthenticationDataService, AuthenticationService } from '../authentication';
import { Logger } from '../logger/services/logger.service';
import { AppNavigationService, DefaultPageRouteService, RouterService } from '../navigation';
import { SessionRefreshCoordinatorService } from '../session-refresh/services/session-refresh-coordinator.service';

import { UrlService } from './url.service';

import { environment } from 'env/environment';

@UntilDestroy()
@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  private readonly appNavigationService = inject(AppNavigationService);
  private readonly authenticationDataService = inject(AuthenticationDataService);
  private readonly authService = inject(AuthenticationService);
  private readonly defaultRoutingService = inject(DefaultPageRouteService);
  private readonly injector = inject(Injector);
  private readonly logger = inject(Logger);
  private readonly routerService = inject(RouterService);
  private readonly urlService = inject(UrlService);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const coreApiUrl = this.urlService.buildServerApiUrl();
    let modifiedReq = request;
    if (request.url.startsWith(coreApiUrl) && environment.clientVersion !== null) {
      modifiedReq = request.clone({
        headers: request.headers.set('X-KH-Client-Version', environment.clientVersion)
      });
    }

    const failedRequestToken = this.getRequestAccessToken(modifiedReq) ??
      this.authenticationDataService.getAccessToken();
    this.getSessionRefreshCoordinator().recordNetworkActivity(modifiedReq.url);

    return next.handle(modifiedReq)
      .pipe(catchError((error) => this.handleErrors(request, error, failedRequestToken)));
  }

  public handleErrors(
    request: HttpRequest<any>,
    error: HttpErrorResponse,
    failedRequestToken?: string | null): Observable<any> {
    if (error instanceof HttpErrorResponse) {
      if (error.status === HTTP_STATUS_CODES.UNAUTHORIZED) {
        if (this.isSessionRefreshRequest(request)) {
          return throwError(() => error);
        }

        if (error.error?.error === 'session_expired') {
          this.logger.error(`Navigation to ${this.appNavigationService.getLastNavigation()} failed`, error);
          const sessionExpired = this.getSessionRefreshCoordinator().handleSessionExpired(failedRequestToken);

          return sessionExpired ? of() : throwError(() => error);
        }

        if (error.error?.error !== 'invalid_grant') {
          this.logger.error(`Navigation to ${this.appNavigationService.getLastNavigation()} failed`, error);
          // if error is invalid_grant then user is in the process of logging in, so no need to handle log out here
          return from(this.redirectOnUnauthorized()).pipe(switchMap(() => of()));
        }
      }

      if (error.status === HTTP_STATUS_CODES.FORBIDDEN) {
        this.logger.error(`Navigation to ${this.appNavigationService.getLastNavigation()} failed`, error);
        return this.authService.isAuthorized()
          .pipe(
            untilDestroyed(this),
            take(1),
            switchMap(async (isAuthorized) => {
              if (isAuthorized) {
                this.authService.setInterruptedUrl(this.routerService.getUrl());
              }

              await this.defaultRoutingService.navigateToForbidden();
            }));
      }
    }

    return throwError(() => error);
  }

  public async redirectOnUnauthorized(): Promise<void> {
    this.authService.logOut();
    await this.defaultRoutingService.navigateToPublicFallback();
  }

  private getRequestAccessToken(request: HttpRequest<any>): string | null {
    const authorization = request.headers.get('Authorization');

    if (!authorization?.startsWith('Bearer ')) {
      return null;
    }

    return authorization.substring('Bearer '.length);
  }

  private isSessionRefreshRequest(request: HttpRequest<any>): boolean {
    return request.url.endsWith('/Auth/RefreshToken');
  }

  private getSessionRefreshCoordinator(): SessionRefreshCoordinatorService {
    return this.injector.get(SessionRefreshCoordinatorService);
  }
}
